/* ═══════════════════════════════════════════════════════════════════════════
   STATE-LEVEL LEGAL DATA — getmyrecords.org

   This file is the source of truth for state-by-state child welfare and
   civil-statute-of-limitations information surfaced on the Records and SOL
   Info pages. It is deliberately split out from App.jsx so a non-developer
   (legal reviewer, attorney, advocate) can read and revise it without
   touching React code.

   --------------------------------------------------------------------------
   How to update an entry
   --------------------------------------------------------------------------
   - Edit the relevant state's object below.
   - Keep the field shape consistent — the UI in App.jsx reads these keys.
   - When you make a verified change, update the `cf` confidence flag and
     bump LAST_REVIEWED below.
   - Cite your source in the `src` field. Prefer primary sources
     (state code) over secondary (CHILD USA, RAINN).

   --------------------------------------------------------------------------
   Field reference
   --------------------------------------------------------------------------
   n   — full state name
   a   — agency abbreviation (DCF, DCS, etc.)
   af  — full agency name
   ad  — agency mailing address (and email if known)
   p   — phone numbers (general line + child-abuse hotline)
   st  — statute citation(s) for the SOL / records access law
   sc  — civil statute of limitations (the legal filing deadline)
   sk  — criminal statute of limitations (when prosecutors have to charge)
   lk  — lookback / revival window status (open, closed, struck down, etc.)
   rn  — administration notes (state vs. county, regions, etc.)
   nt  — additional context, gotchas, history
   src — source(s) cross-referenced when this entry was last verified
   cf  — confidence flag: "high" | "medium" | "low"
         high   = cross-referenced against authoritative source, recent
         medium = broadly correct, may be missing 2025 nuances
         low    = known data conflict or unverified — needs human review

   --------------------------------------------------------------------------
   Records-request schema (rr)
   --------------------------------------------------------------------------
   The `rr` (records-request) sub-object drives the state-aware letter
   generator. It is DIFFERENT from the SOL data above. SOL controls civil
   filing deadlines; rr controls how to request your records from the
   agency. They use different statutes, different forms, and different
   procedures. Conflating them produces wrong letters.

   When `rr` is present, the letter generator uses these fields. When `rr`
   is missing, the generator falls back to a generic template and shows the
   user a prominent "we have not verified this state's procedures" warning.

   Fields (all optional except `verified` and `source`):

   authority         — Statute(s) governing records access (NOT the SOL
                       statute). E.g., "Texas Public Information Act,
                       Government Code Chapter 552; Family Code § 261.201"
   responseDays      — Number of days the agency typically has to respond.
   responseDaysType  — "business" | "calendar"
   responseNote      — Free text clarifying the response timeframe.

   formRequired      — Name of a state-specific intake form, or null.
                       E.g., "CFS Form 600-3A". If set, the generator will
                       tell the user they need this form INSTEAD of (or in
                       addition to) the generated letter.
   formUrl           — Direct link to the form, or null.
   formNote          — How the form is used / where to send it.

   idRequired        — true | false. Whether photo ID is required.
   idNote            — How to send ID (mail copy, scan, in-person, etc.).

   thirdParty        — "yes" | "no" | "limited" | "court_order"
                       For requests by someone other than the subject.
                       The generator will surface a hard warning if a user
                       attempts a third-party request in a state where
                       this is "no" or "court_order".
   thirdPartyNote    — Specific limits or required documentation.

   submitMethods     — Array. Any of "mail", "email", "portal", "fax".
   submitEmail       — Records request email if applicable.
   submitPortal      — URL to online portal if applicable.

   notes             — Free-text procedural notes (CPS confidentiality,
                       county vs state custodian, etc.).

   verified          — true | false. Has someone actually verified this
                       against the state agency's current published
                       guidance? When false, the UI shows a warning.
   source            — Source URL or name + date last verified.

   --------------------------------------------------------------------------
   Verification status
   --------------------------------------------------------------------------
   Last full review: April 16, 2026
   Source: CHILD USA 2025 SOL Tracker (https://childusa.org/2025-sol-tracker/)

   States needing primary-source verification before launch:
   - MS — direct conflict between sources, marked cf:"low"
   - TX — recent SB 2798 nuance: civil SOL extended (not eliminated)
   - IN — BSA window: prior data said closed, CHILD USA says open
   ═══════════════════════════════════════════════════════════════════════════ */

export const LAST_REVIEWED = "April 16, 2026";

export const STATES_DATA = {
  AL: {
    n: "Alabama",
    a: "DHR",
    af: "Alabama Department of Human Resources",
    ad: "Alabama Department of Human Resources, Gordon Persons Building, 50 Ripley Street, Montgomery, AL 36130",
    p: "(334) 242-1310 (DHR general), (334) 242-9500 (child abuse hotline)",
    st: "Alabama Code § 26-14-8; Code § 6-2-38 (general SOL)",
    sc: "2 years from the date of injury. NO discovery rule. Clock runs from last act. Closes at age 21 (age of majority is 19). One of the worst general civil SOLs in the country for survivors.",
    sk: "No SOL for first-degree rape, sodomy, sexual abuse of child under 16.",
    lk: "NARROW LOOKBACK OPEN: 2024 revival window for claims against federally chartered organizations that initiated bankruptcy on February 18, 2020 (Boy Scouts of America). General CSA reform bills SB44/HB120 still pending 2025-2026.",
    rn: "County-administered through 67 county offices.",
    nt: "Generally one of the worst states for civil SOL — but if your abuse involved a federally chartered organization (e.g., BSA), the 2024 revival window may apply. Check with an attorney handling BSA-related claims.",
    src: "CHILD USA SOL Tracker (April 2026)",
    cf: "medium",
  },
  AK: {
    n: "Alaska",
    a: "OCS",
    af: "Alaska Office of Children's Services",
    ad: "OCS, P.O. Box 110630, Juneau, AK 99811",
    p: "(907) 465-3170, (800) 478-4444 (hotline)",
    st: "AS 09.10.065 (CSA civil SOL); AS 47.17.040",
    sc: "NO civil SOL for: felony sex abuse of a minor (since 2001), unlawful exploitation of a minor (since 2003), and sex trafficking (since 2013). Other CSA claims still subject to 3 years from discovery.",
    sk: "No SOL for sexual abuse of minor in first degree.",
    lk: "No traditional retroactive lookback. Civil SOL was eliminated prospectively for the offenses listed above on the dates noted.",
    rn: "State-administered. Vast geography.",
    nt: "Stronger than older summaries suggest. Civil SOL eliminated for the most serious child sex offenses (felony sex abuse, exploitation, trafficking). Other claims still subject to 3-year discovery rule. ICWA critical for Alaska Native population.",
    src: "CHILD USA SOL Tracker (April 2026)",
    cf: "high",
  },
  AZ: {
    n: "Arizona",
    a: "DCS",
    af: "Arizona Department of Child Safety",
    ad: "DCS, 3003 North Central Avenue, Phoenix, AZ 85012",
    p: "(602) 255-2500, (888) 767-2445 (hotline)",
    st: "ARS § 8-807",
    sc: "2 years from injury. Discovery rule may toll. Relatively short.",
    sk: "No SOL for sexual conduct with a minor or molestation of a child.",
    lk: "No lookback window.",
    rn: "State-administered. Created 2014 as standalone agency.",
    nt: "Foster Youth Bill of Rights allows copies of all records upon leaving care.",
    cf: "medium",
  },
  AR: {
    n: "Arkansas",
    a: "DCFS",
    af: "Arkansas Division of Children and Family Services",
    ad: "DCFS, P.O. Box 1437, Slot S560, Little Rock, AR 72203",
    p: "(501) 682-8770, (800) 482-5964 (hotline)",
    st: "Arkansas Code § 12-18-901 et seq.; SB13/Act 49 (2025)",
    sc: "NO civil SOL for childhood sexual abuse — eliminated 2023. Survivors can file at any time. (An earlier 2021 retroactive revival was struck down Feb 2025, but the prospective elimination stands.)",
    sk: "No SOL for first-degree rape or sexual assault. SB 431/Act 987 (2025) eliminated criminal SOL for human-trafficking-related offenses including sexual extortion involving minors.",
    lk: "ACTIVE: 2024-2026 revival window OPEN (opened February 1, 2024). SB13/Act 49 (Feb 17, 2025) added a 1-year revival window for CSA claims against entities in bankruptcy.",
    rn: "State-administered through 10 area offices.",
    nt: "Better than it appears. Civil SOL eliminated for new claims, plus an active retroactive revival window through 2026 against entities. The 2021 retroactive window was struck down but the 2023 prospective elimination is intact.",
    src: "CHILD USA SOL Tracker (April 2026); Arkansas Acts 49 & 987 of 2025",
    cf: "high",
  },
  CA: {
    n: "California",
    a: "DSS/County CWS",
    af: "California Department of Social Services (county-administered)",
    ad: "County-administered — contact your county child welfare agency. State: CDSS, 744 P Street, Sacramento, CA 95814",
    p: "Varies by county. State: (916) 651-8100",
    st: "California Welfare & Institutions Code § 827",
    sc: "No SOL for post-2024 abuse (AB 452). Pre-2024: age 40 or 5 years from discovery. AB 250 lookback open 2026-2027.",
    sk: "No SOL for child sexual abuse felonies.",
    lk: "AB 218 CLOSED. AB 250 open 2026-2027 for adult survivors.",
    rn: "County-administered through 58 county agencies.",
    nt: "Strong protections post-2024. AB 250 lookback window currently ACTIVE.",
    cf: "high",
    rr: {
      authority: "California Public Records Act, Government Code § 7920 et seq.; Welfare & Institutions Code § 827 (juvenile case files)",
      responseDays: 10,
      responseDaysType: "calendar",
      responseNote: "California PRA requires a response within 10 calendar days (extendable by 14 more in 'unusual circumstances'). Juvenile case files under § 827 are sealed and require a separate court order in most cases — they are NOT released through a routine PRA request.",
      formRequired: "Juvenile court Form JV-570 (petition to access juvenile case file)",
      formUrl: "https://courts.ca.gov/forms?query=jv-570",
      formNote: "For sealed juvenile dependency or delinquency files (which is what most child-welfare records are), you must petition the juvenile court using Form JV-570 — a routine PRA letter to the county will not produce them.",
      idRequired: true,
      idNote: "ID required. Each county sets its own verification process.",
      thirdParty: "court_order",
      thirdPartyNote: "Juvenile case files are sealed. Even close relatives generally need a court order under Welfare & Institutions Code § 827. The subject themselves can typically access their own file as an adult, but may still need to petition the juvenile court.",
      submitMethods: ["mail", "portal"],
      submitEmail: null,
      submitPortal: null,
      notes: "California is county-administered through 58 county child welfare agencies. There is NO single state records inbox. The state CDSS does not hold case records. You must contact your specific county. The juvenile court process under § 827 is the actual path for most child-welfare records — the standard PRA letter generated here is a starting point but will likely be redirected to the juvenile court.",
      verified: false,
      source: "California Welfare & Institutions Code § 827 + Judicial Council form JV-570. Verify with your county and with a juvenile court self-help center before relying.",
    },
  },
  CO: {
    n: "Colorado",
    a: "CDHS",
    af: "Colorado Department of Human Services",
    ad: "County-administered — contact your county DHS. State: 1575 Sherman Street, Denver, CO 80203",
    p: "Varies by county. State: (303) 866-5700",
    st: "CRS § 13-80-103.7 (CSA SOL); CRS § 19-1-307",
    sc: "NO civil SOL for sexual assault of a minor — eliminated 2021. (A separate new cause of action for sexual misconduct against minors was added 2022.) The 2022 retroactive revival window for previously expired claims was held unconstitutional in 2023, but the prospective elimination remains intact.",
    sk: "No SOL for sexual assault on a child.",
    lk: "Civil SOL ELIMINATED prospectively (2021). Retroactive 2022-2025 lookback window was STRUCK DOWN as unconstitutional by the Colorado Supreme Court in 2023.",
    rn: "County-administered through 64 county departments.",
    nt: "Mixed picture. New abuse: no civil SOL. Older expired claims: the retroactive revival window was struck down in 2023, so previously time-barred claims remain barred.",
    src: "CHILD USA SOL Tracker (April 2026)",
    cf: "high",
  },
  CT: {
    n: "Connecticut",
    a: "DCF",
    af: "Connecticut Department of Children and Families",
    ad: "DCF Legal Division, 505 Hudson Street, Hartford, CT 06106",
    p: "(860) 550-6300, (800) 842-2288 (Careline)",
    st: "Connecticut General Statutes § 17a-28",
    sc: "30 years from age 18 (until age 48) or 30 years from discovery. Claims Commissioner can bypass SOL for claims against state.",
    sk: "No SOL for first-degree sexual assault. 30 years from age 18 for others.",
    lk: "No traditional window. Claims Commissioner process is alternative route.",
    rn: "State-administered through area offices.",
    nt: "Claims Commissioner process bypasses SOL for state claims. A survivor who was raped thousands of times while under DCF monitoring filed a $30M claim through this pathway — the Claims Commissioner authorized the lawsuit even though the standard SOL had issues.",
    cf: "high",
  },
  DE: {
    n: "Delaware",
    a: "DFS",
    af: "Delaware Division of Family Services",
    ad: "DFS, 1825 Faulkland Road, Wilmington, DE 19805",
    p: "(302) 633-2500, (800) 292-9582 (hotline)",
    st: "16 Del. C. § 907",
    sc: "No SOL — permanently eliminated. Survivors can file at any time.",
    sk: "No SOL for rape/sexual abuse of child under 18.",
    lk: "SOL eliminated entirely. Pioneer state (first lookback window 2007-2009, now no SOL needed).",
    rn: "State-administered. Small state — centralized.",
    nt: "One of the best states — no civil SOL at all.",
    cf: "medium",
  },
  FL: {
    n: "Florida",
    a: "DCF",
    af: "Florida Department of Children and Families",
    ad: "DCF, 2415 North Monroe Street, Suite 400, Tallahassee, FL 32303. Email: records.request@myflfamilies.com",
    p: "(850) 487-1111, (800) 962-2873 (hotline)",
    st: "Florida Statutes § 39.202",
    sc: "No SOL for victims under 16 (permanent elimination since 2010). Age 16-17: until age 25 or 4 years from discovery.",
    sk: "No SOL for any sexual offense against a minor (Donna's Law).",
    lk: "Permanent elimination for under-16 victims. Not retroactive to pre-2010 expired claims.",
    rn: "State-administered through circuit offices.",
    nt: "Strong protections. Records maintained until child's 30th birthday.",
    cf: "high",
    rr: {
      authority: "Florida Public Records Law, Chapter 119, Florida Statutes; § 39.202 (CPS confidentiality)",
      responseDays: null,
      responseDaysType: null,
      responseNote: "Florida law requires a 'reasonable time' to respond — there is no fixed statutory deadline. CPS records typically take several weeks because the agency must review for confidentiality redactions under § 39.202.",
      formRequired: null,
      formUrl: null,
      idRequired: true,
      idNote: "Include a copy of your photo ID with the request.",
      thirdParty: "limited",
      thirdPartyNote: "CPS records are confidential. Parents, legal guardians, and attorneys generally qualify; other relatives and family members typically need a court order.",
      submitMethods: ["email", "mail"],
      submitEmail: "records.request@myflfamilies.com",
      submitPortal: null,
      notes: "DCF is state-administered through circuit offices; the central records request inbox routes to the correct circuit. Records are maintained until the subject's 30th birthday.",
      verified: false,
      source: "DCF records-request guidance — verify at myflfamilies.com before sending.",
    },
  },
  GA: {
    n: "Georgia",
    a: "DFCS",
    af: "Georgia Division of Family and Children Services",
    ad: "DFCS, 2 Peachtree Street NW, Suite 19-490, Atlanta, GA 30303. County-administered — contact county DFCS.",
    p: "(404) 651-8746, (855) 422-4453 (hotline)",
    st: "O.C.G.A. § 49-5-40 through 49-5-44",
    sc: "Age 23 (5 years after majority). One of worst in country. Hidden Predator Act lookback excluded institutions.",
    sk: "No SOL for sexual offenses against children under 16.",
    lk: "CLOSED (2015-2017). Excluded institutional defendants. Efforts to fix have stalled.",
    rn: "County-administered through 159 county offices.",
    nt: "One of worst states for survivors. Age 23 SOL, excluded institutions from lookback.",
    cf: "high",
  },
  HI: {
    n: "Hawaii",
    a: "CWS",
    af: "Hawaii Child Welfare Services",
    ad: "CWS, 1010 Richards Street, Suite 400, Honolulu, HI 96813",
    p: "(808) 586-5680, (808) 832-5300 (hotline, Oahu)",
    st: "HRS § 350-1.4",
    sc: "Age 24 or 3 years from discovery. Three prior lookback windows all closed.",
    sk: "No SOL for first-degree sexual assault of child under 14.",
    lk: "Three windows, all CLOSED (most recent 2020).",
    rn: "Operates across 4 counties.",
    nt: "Lookback windows all closed. Civil SOL until age 24 is moderate.",
    cf: "medium",
  },
  IA: {
    n: "Iowa",
    a: "HHS",
    af: "Iowa Department of Health and Human Services",
    ad: "HHS, 1305 East Walnut Street, Des Moines, IA 50319",
    p: "(515) 281-5521, (800) 362-2178 (hotline)",
    st: "Iowa Code § 614.1 (CSA SOL); Iowa Code § 235A.15",
    sc: "2 years from discovery for general civil claims. Relatively short.",
    sk: "No SOL for sexual abuse of child under 18 (first through third degree).",
    lk: "NARROW LOOKBACK OPEN: 2024 revival window for claims against federally chartered organizations that initiated bankruptcy on February 18, 2020 (Boy Scouts of America). No general retroactive lookback for other claims.",
    rn: "State-administered. Agency reorganized from DHS to HHS in 2023.",
    nt: "Short general civil SOL — but if your abuse involved a federally chartered organization (e.g., BSA), the 2024 revival window may apply. Older records may reference DHS.",
    src: "CHILD USA SOL Tracker (April 2026)",
    cf: "medium",
  },
  ID: {
    n: "Idaho",
    a: "DHW",
    af: "Idaho Department of Health and Welfare",
    ad: "DHW, 450 West State Street, Boise, ID 83720",
    p: "(208) 334-5500, (855) 552-5437 (hotline)",
    st: "Idaho Code § 16-1629",
    sc: "5 years from age 18 (until age 23). Discovery rule may apply.",
    sk: "No SOL for lewd conduct with child under 16.",
    lk: "No lookback window.",
    rn: "State-administered through 7 regions.",
    nt: "Short civil SOL. No lookback window.",
    cf: "medium",
  },
  IL: {
    n: "Illinois",
    a: "DCFS",
    af: "Illinois Department of Children and Family Services",
    ad: "FOIA Officer, DCFS Office of Communications, JRTC 6th Floor, 100 W. Randolph Street, Chicago, IL 60601. Fax: 312-814-7092",
    p: "(312) 814-6800, (800) 232-3798 (Advocacy Office)",
    st: "325 ILCS 5/11.1 (ANCRA), 89 Ill. Admin. Code 431",
    sc: "No SOL for post-2014 abuse. Pre-2014: age 38 or 20 years from discovery. Tolled for fraudulent concealment.",
    sk: "No SOL for felony child sexual abuse (eliminated 2017) or any sexual offense (2019).",
    lk: "No traditional window needed — eliminated SOL for post-2014 abuse. Tolling for concealment extends pre-2014 claims.",
    rn: "State-administered. CPS records NOT available through FOIA — separate ANCRA process required.",
    nt: "Strong protections. CPS records require CFS 600-3A form, NOT FOIA. DCFS Advocacy Office at (800) 232-3798 helps navigate.",
    cf: "high",
    rr: {
      authority: "Illinois Abused and Neglected Child Reporting Act (ANCRA), 325 ILCS 5; 89 Illinois Administrative Code 431",
      responseDays: 30,
      responseDaysType: "calendar",
      responseNote: "Illinois CPS records are NOT subject to FOIA. The ANCRA process typically takes 30 days or longer. The DCFS Advocacy Office (800-232-3798) can help if you don't get a response.",
      formRequired: "CFS 600-3A (Request for Access to and Confidential Disclosure of Records)",
      formUrl: "https://www2.illinois.gov/dcfs/aboutus/Documents/CFS_600-3A.pdf",
      formNote: "Illinois requires CFS Form 600-3A for CPS records access — a free-form letter alone will usually be rejected. Submit the form along with the letter. The form asks for your relationship to the subject, dates of involvement, and what you're requesting.",
      idRequired: true,
      idNote: "Notarized signature required. Photo ID copy must be attached.",
      thirdParty: "limited",
      thirdPartyNote: "Third-party access is restricted. Parents, legal guardians, the subject's attorney, and named perpetrators (for their own records) can request. Other relatives typically need a court order.",
      submitMethods: ["mail", "fax"],
      submitEmail: null,
      submitPortal: null,
      notes: "ILLINOIS IS DIFFERENT. Do NOT send a generic FOIA letter — they will reject it. Use Form CFS 600-3A. The DCFS Advocacy Office at (800) 232-3798 can walk you through the process and is the best resource if anything goes wrong.",
      verified: false,
      source: "DCFS Office of Communications + ANCRA. Verify the form revision date before sending — DCFS occasionally updates the form.",
    },
  },
  IN: {
    n: "Indiana",
    a: "DCS",
    af: "Indiana Department of Child Services",
    ad: "DCS, 302 West Washington Street, Room E306, Indianapolis, IN 46204",
    p: "(888) 631-9510, (800) 800-5556 (hotline)",
    st: "Indiana Code § 34-11-2-4 (CSA SOL); Indiana Code § 31-33-18",
    sc: "7 years from age 18 (until age 25) for general civil claims.",
    sk: "No SOL for child molesting (Level 1 felony) or rape of a child.",
    lk: "BSA-SPECIFIC LOOKBACK STATUS UNCERTAIN: per CHILD USA April 2026, the revival window for claims against Boy Scouts of America (federally chartered org bankruptcy beginning Feb 18, 2020) is CURRENTLY OPEN. HB 1529 (introduced Jan 2025) would extend this window to July 1, 2026. A prior version of this page incorrectly said the window closed July 2025 — verify the current status with an attorney handling BSA-related claims before relying on either date.",
    rn: "State-administered through regional offices.",
    nt: "DATA UPDATED. Earlier version of this page stated the BSA window closed July 2025; CHILD USA's April 2026 review lists it as open. Confirm current status before filing.",
    src: "CHILD USA SOL Tracker (April 2026); Indiana HB 1529 (2025)",
    cf: "medium",
  },
  KS: {
    n: "Kansas",
    a: "DCF",
    af: "Kansas Department for Children and Families",
    ad: "DCF, 555 South Kansas Avenue, Topeka, KS 66603",
    p: "(785) 296-3271, (800) 922-5330 (hotline)",
    st: "Kansas Statutes § 60-523 (CSA SOL); § 38-2209",
    sc: "3 years from age 18 (until age 21) is the historical SOL — but a 2023 revival extends the filing window to age 31, OR within 3 years after a criminal conviction of the abuser, whichever applies.",
    sk: "No SOL for rape, aggravated criminal sodomy, or aggravated indecent liberties with a child.",
    lk: "REVIVAL OPEN since 2023 — extends civil filing to age 31 or 3 years after criminal conviction. Survivors who thought they were past the deadline at age 21 may now have additional time.",
    rn: "State-administered but contracts some services to private agencies.",
    nt: "Better than older summaries suggest. The 2023 revival to age 31 (or 3 years post-conviction) significantly expands what survivors can do.",
    src: "CHILD USA SOL Tracker (April 2026)",
    cf: "high",
  },
  KY: {
    n: "Kentucky",
    a: "DCBS",
    af: "Kentucky Department for Community Based Services",
    ad: "DCBS, 275 East Main Street, Frankfort, KY 40621",
    p: "(502) 564-2136, (877) 597-2331 (hotline)",
    st: "KRS § 413.249; KRS § 620.050",
    sc: "5 years from last act or 5 years from age 18 (until age 23). Discovery rule may extend.",
    sk: "No SOL for first-degree rape, sodomy, or sexual abuse of child under 18.",
    lk: "NO ACTIVE LOOKBACK. A 2021 revival window (which would have revived expired claims up to 5 years after expiration) was held UNCONSTITUTIONAL by Kentucky courts in 2024. HB 47 and HB 232 (introduced Feb 2025) would create new SOL extensions / eliminate civil SOL — both still in committee.",
    rn: "State-administered through 9 service regions.",
    nt: "The 2021 retroactive revival was struck down in 2024, so previously expired claims remain barred. Pending 2025 legislation could improve this. Kentucky AG's office handles open records appeals.",
    src: "CHILD USA SOL Tracker (April 2026)",
    cf: "medium",
  },
  LA: {
    n: "Louisiana",
    a: "DCFS",
    af: "Louisiana Department of Children and Family Services",
    ad: "DCFS, 627 North 4th Street, Baton Rouge, LA 70802",
    p: "(225) 342-0286, (855) 452-5437 (hotline)",
    st: "Louisiana Children's Code Article 615",
    sc: "No SOL — eliminated in 2021. Survivors can file at any time.",
    sk: "No SOL for aggravated rape, forcible rape, or sexual battery of victim under 17.",
    lk: "ACTIVE through June 14, 2027. Allows previously time-barred claims against individuals and organizations.",
    rn: "State-administered through 9 regional offices.",
    nt: "One of strongest states. No civil SOL and ACTIVE lookback window through June 2027.",
    cf: "high",
  },
  MA: {
    n: "Massachusetts",
    a: "DCF",
    af: "Massachusetts Department of Children and Families",
    ad: "DCF, 600 Washington Street, 6th Floor, Boston, MA 02111. Online portal: mass.gov/department-of-children-families-record-requests",
    p: "(617) 748-2400, (617) 748-2444 (Ombudsman), (800) 792-5200 (hotline)",
    st: "MGL c. 119, § 51A-51G; 110 CMR 12.00",
    sc: "35 years from the abuse or 7 years from discovery, whichever gives more time. Effectively until age 53. No lookback window.",
    sk: "No SOL. After 27 years, charges need independent corroborating evidence.",
    lk: "NO lookback window. Bills to create one have not passed. Enough Abuse campaigns for reform.",
    rn: "State-administered with area offices. Online records portal available.",
    nt: "Strong SOL (age 53) but NO lookback window. Charitable immunity cap limits institutional liability. Enough Abuse (MA-based) is key advocacy org.",
    cf: "high",
    rr: {
      authority: "Massachusetts Public Records Law, M.G.L. c. 66 § 10; 110 CMR 12.00 (DCF records regulations)",
      responseDays: 10,
      responseDaysType: "business",
      responseNote: "Massachusetts Public Records Law requires custodians to respond within 10 business days. CPS records have additional confidentiality review and may take longer.",
      formRequired: null,
      formUrl: null,
      formNote: "Massachusetts has an online records-request portal that's the easiest path — see submitPortal below. A written letter to the address still works.",
      idRequired: true,
      idNote: "Photo ID copy required. The online portal will prompt for upload.",
      thirdParty: "limited",
      thirdPartyNote: "Third-party access is restricted to parents, legal guardians, the subject's attorney, or those with a court order. The Ombudsman at (617) 748-2444 can help navigate complex situations.",
      submitMethods: ["portal", "mail"],
      submitEmail: null,
      submitPortal: "https://www.mass.gov/department-of-children-families-record-requests",
      notes: "Use the online portal when possible — it's faster than mail. The DCF Ombudsman office (617-748-2444) is a good resource if your request stalls.",
      verified: false,
      source: "Mass.gov DCF records request portal — verify the URL is current before linking.",
    },
  },
  MD: {
    n: "Maryland",
    a: "DHS/SSA",
    af: "Maryland Department of Human Services, Social Services Administration",
    ad: "SSA, 311 West Saratoga Street, Baltimore, MD 21201. County-administered — contact local DSS.",
    p: "(800) 332-6347, (410) 767-7506",
    st: "Maryland Cts. & Jud. Proc. § 5-117 (CSA SOL); Family Law Article § 5-707; HB 1378 (2025)",
    sc: "NO civil SOL — permanently and retroactively eliminated in 2023 (Child Victims Act). Survivors can file at any time. IMPORTANT NEW LIMIT: HB 1378 (2025, effective June 1, 2025) PROHIBITS new claims AGAINST THE STATE OF MARYLAND filed after January 1, 2026, and caps non-economic damages. Claims against private defendants remain unrestricted.",
    sk: "No SOL for felony sexual offenses against minors.",
    lk: "PERMANENT — retroactive SOL elimination 2023, intact for private defendants. State-defendant claims must be filed by January 1, 2026 (HB 1378).",
    rn: "County-administered through 24 local DSS (one per county + Baltimore City).",
    nt: "Critical 2025 update: if you have a claim AGAINST THE STATE OF MARYLAND (e.g., DHS, state-run institution), you MUST file before January 1, 2026 — HB 1378 caps damages and bars new state-defendant claims after that date. Private-defendant claims still have no deadline.",
    src: "CHILD USA SOL Tracker (April 2026); Maryland HB 1378 (2025)",
    cf: "high",
    rr: {
      authority: "Maryland Public Information Act, GP § 4-101 et seq.; Family Law Article § 5-707 (CPS confidentiality)",
      responseDays: 30,
      responseDaysType: "calendar",
      responseNote: "MD PIA requires a response within 30 calendar days. CPS records require additional confidentiality review.",
      formRequired: null,
      formUrl: null,
      idRequired: true,
      idNote: "Photo ID required.",
      thirdParty: "limited",
      thirdPartyNote: "Subjects, parents, legal guardians, and attorneys can typically access. Other parties generally need a court order.",
      submitMethods: ["mail"],
      submitEmail: null,
      submitPortal: null,
      notes: "MARYLAND IS COUNTY-ADMINISTERED through 24 local Departments of Social Services (one per county + Baltimore City). The state SSA does not hold case records — contact your local DSS. URGENT: if your case is against the State of Maryland (e.g., a state-run institution), HB 1378 (2025) imposes a January 1, 2026 deadline to file new claims and caps damages. This is a SOL deadline, not a records-request deadline, but you should request your records well before then.",
      verified: false,
      source: "MD PIA + Family Law § 5-707 + HB 1378 (2025). Verify your local DSS submission process.",
    },
  },
  ME: {
    n: "Maine",
    a: "OCFS",
    af: "Maine Office of Child and Family Services",
    ad: "OCFS, 2 Anthony Avenue, Augusta, ME 04333",
    p: "(207) 624-7900, (800) 452-1999 (hotline)",
    st: "22 MRSA § 4008; 14 MRSA § 752-C (civil SOL)",
    sc: "NO civil SOL for sexual acts/contact with a minor — eliminated April 7, 2000. Survivors can file at any time.",
    sk: "No SOL for gross sexual assault of child under 16.",
    lk: "PERMANENT lookback window OPEN since 2021. Revives all expired claims against any defendant.",
    rn: "State-administered through 8 districts. Foster youth get copies of all records.",
    nt: "One of the strongest states. No civil SOL since 2000 plus a permanent open revival window since 2021. Foster youth also get full records access.",
    src: "CHILD USA SOL Tracker (April 2026)",
    cf: "high",
  },
  MI: {
    n: "Michigan",
    a: "DHHS/CPS",
    af: "Michigan Department of Health and Human Services, Children's Protective Services",
    ad: "MDHHS, 235 South Grand Avenue, Lansing, MI 48909",
    p: "(517) 335-6158, (855) 444-3911 (hotline)",
    st: "MCL § 600.5851b (CSA SOL); MCL § 722.627",
    sc: "Age 28 or 3 years from discovery for general CSA claims. A 2018 revival window applied ONLY to victims of Larry Nassar (90-day window, since closed). Justice for Survivors bill pending in House would expand this.",
    sk: "No SOL for first-degree criminal sexual conduct.",
    lk: "NO ACTIVE general lookback. Prior 2018 90-day Nassar-only window CLOSED. Justice for Survivors legislation pending in 2025 would create a broader revival window.",
    rn: "State-administered through county-based offices.",
    nt: "Moderate general SOL. The 2018 revival window helped only Nassar survivors. Pending 2025 legislation could broaden things significantly.",
    src: "CHILD USA SOL Tracker (April 2026)",
    cf: "medium",
    rr: {
      authority: "Michigan Freedom of Information Act, MCL § 15.231 et seq.; Child Protection Law, MCL § 722.627 (CPS confidentiality)",
      responseDays: 5,
      responseDaysType: "business",
      responseNote: "MI FOIA requires response within 5 business days, with a 10-business-day extension allowed. CPS records under MCL § 722.627 may take longer due to confidentiality review.",
      formRequired: null,
      formUrl: null,
      idRequired: true,
      idNote: "Photo ID copy required for records that identify the subject.",
      thirdParty: "limited",
      thirdPartyNote: "CPS records are confidential. Parents, legal guardians, attorneys, the subject themselves, and certain investigators can access. Other parties typically need a court order.",
      submitMethods: ["mail", "email"],
      submitEmail: null,
      submitPortal: null,
      notes: "MDHHS is state-administered with county-based offices. Send the request to the central MDHHS FOIA coordinator in Lansing. MDHHS has a records request form on their website but a written letter is also accepted.",
      verified: false,
      source: "MDHHS FOIA guidance. Verify the current submission address and whether a standard FOIA form is preferred.",
    },
  },
  MN: {
    n: "Minnesota",
    a: "DHS",
    af: "Minnesota Department of Human Services",
    ad: "DHS, 540 Cedar Street, P.O. Box 64244, St. Paul, MN 55164. County-administered — contact county social services.",
    p: "(651) 431-4661, (651) 291-0211 (reporting)",
    st: "Minn. Stat. § 541.073 (CSA SOL); Minn. Stat. § 626.556; Chapter 13 (Data Practices Act)",
    sc: "NO civil SOL for sexual abuse of a minor — eliminated effective May 25, 2013 (applies to non-expired claims as of that date).",
    sk: "No SOL for criminal sexual conduct 1st-4th degree when victim under 18.",
    lk: "Minnesota Child Victims Act revival window for previously expired claims CLOSED (2013-2016). Going forward, no SOL applies.",
    rn: "County-administered through 87 county agencies. Data Practices Act gives subjects right to own records.",
    nt: "Stronger than older summaries suggest — Minnesota eliminated the civil SOL in 2013. The 2013-2016 retroactive window is closed, but new claims have no deadline. Data Practices Act provides strong records access.",
    src: "CHILD USA SOL Tracker (April 2026)",
    cf: "high",
  },
  MO: {
    n: "Missouri",
    a: "CD",
    af: "Missouri Children's Division",
    ad: "Children's Division, 205 Jefferson Street, P.O. Box 88, Jefferson City, MO 65103",
    p: "(573) 751-3221, (800) 392-3738 (hotline)",
    st: "RSMo § 210.150",
    sc: "Age 31 (10+3 years after turning 18). 3-year extension after criminal conviction.",
    sk: "No SOL for first-degree rape or sodomy.",
    lk: "No lookback window.",
    rn: "State-administered through circuit offices. Agency called 'Children's Division.'",
    nt: "Moderate SOL. 3-year extension after conviction provides alternative path.",
    cf: "medium",
  },
  MS: {
    n: "Mississippi",
    a: "DCPS",
    af: "Mississippi Department of Child Protection Services",
    ad: "DCPS, 750 North State Street, Jackson, MS 39202",
    p: "(601) 359-4991, (800) 222-8000 (hotline)",
    st: "Mississippi Code § 43-21-261",
    sc: "DATA CONFLICT — needs primary-source verification. Our prior data claimed an active 2024-2027 revival window. CHILD USA's SOL Tracker (April 2026) lists Mississippi as having NO active revival window, and HB 486 (which would have eliminated civil SOL) died in committee Feb 2025. Do NOT rely on this entry for filing decisions until verified against the Mississippi Code.",
    sk: "No SOL for sexual battery of child under 16.",
    lk: "DATA CONFLICT — see civil deadline note above. Verify with an attorney or the Mississippi Code before relying on any window.",
    rn: "State-administered. Created 2016 as standalone agency.",
    nt: "This page has an unresolved data conflict between sources. Until verified against the actual Mississippi statute, treat any deadline information here as needing attorney confirmation. Age of majority is 21.",
    src: "DISCREPANCY between earlier source and CHILD USA SOL Tracker (April 2026). Pending primary-source review.",
    cf: "low",
  },
  MT: {
    n: "Montana",
    a: "CFSD",
    af: "Montana Child and Family Services Division",
    ad: "CFSD, 1400 Broadway, P.O. Box 8005, Helena, MT 59604",
    p: "(406) 444-5900, (866) 820-5437 (hotline)",
    st: "MCA § 41-3-205",
    sc: "Must file before age 27. Discovery rule may apply.",
    sk: "No SOL for sexual intercourse without consent when victim under 16.",
    lk: "No lookback window.",
    rn: "State-administered through 5 regions.",
    nt: "Short SOL (age 27). Significant tribal population — ICWA considerations.",
    cf: "medium",
  },
  NC: {
    n: "North Carolina",
    a: "DSS",
    af: "North Carolina Division of Social Services",
    ad: "DSS, 820 South Boylan Avenue, Raleigh, NC 27603. County-administered — contact county DSS.",
    p: "(919) 527-6335",
    st: "N.C.G.S. § 1-17(d) (CSA SOL); N.C.G.S. § 108A-80; Chapter 7B",
    sc: "Age 28 under SAFE Child Act (2019). Alternative: 2 years after criminal conviction of the abuser. Going-forward SOL only — the 2020-2021 retroactive window has closed.",
    sk: "No SOL for felony child sexual offenses.",
    lk: "NO ACTIVE LOOKBACK. SAFE Child Act opened a 2-year revival window (Jan 1, 2020 – Dec 31, 2021) for previously expired civil claims; that window has CLOSED. Going forward, SOL extends to age 28.",
    rn: "County-administered through 100 county DSS agencies. Fostering Care in NC Act (2025) changed oversight.",
    nt: "Moderate SOL. The 2020-2021 SAFE Child Act revival is closed — older expired claims are barred again. 2025 Fostering Care Act changes accountability structures going forward.",
    src: "CHILD USA SOL Tracker (April 2026)",
    cf: "high",
  },
  ND: {
    n: "North Dakota",
    a: "HHS",
    af: "North Dakota Department of Health and Human Services",
    ad: "HHS, 600 East Boulevard Avenue, Dept 325, Bismarck, ND 58505",
    p: "(701) 328-2316, (833) 958-3500 (hotline)",
    st: "NDCC § 50-25.1-11",
    sc: "2 years from discovery or from age 18. Very short.",
    sk: "No SOL for gross sexual imposition of child under 15.",
    lk: "No lookback window.",
    rn: "County-administered through 8 human service zones.",
    nt: "One of shortest civil SOLs. No lookback window.",
    cf: "medium",
  },
  NE: {
    n: "Nebraska",
    a: "CFS",
    af: "Nebraska Division of Children and Family Services",
    ad: "CFS, 301 Centennial Mall South, P.O. Box 95026, Lincoln, NE 68509",
    p: "(402) 471-3121, (800) 652-1999 (hotline)",
    st: "Neb. Rev. Stat. § 28-722",
    sc: "12 years from age 19 (until age 31). Age of majority is 19.",
    sk: "No SOL for first-degree sexual assault of a child.",
    lk: "No lookback window.",
    rn: "State-administered through 5 service areas. Age of majority is 19.",
    nt: "Moderate SOL (age 31). Age of majority is 19.",
    cf: "medium",
  },
  NH: {
    n: "New Hampshire",
    a: "DCYF",
    af: "New Hampshire Division for Children, Youth and Families",
    ad: "DCYF, 129 Pleasant Street, Concord, NH 03301",
    p: "(603) 271-6562, (800) 894-5533 (hotline)",
    st: "RSA 508:4-g; RSA 169-C:25",
    sc: "NO civil SOL for sexual assault or related offenses against minors and adults — eliminated effective September 18, 2020. Survivors can file at any time.",
    sk: "No SOL for aggravated felonious sexual assault of child under 18.",
    lk: "Civil SOL ELIMINATED Sept 2020. No traditional retroactive lookback window — verify with an attorney how the elimination applies to abuse predating Sept 2020.",
    rn: "State-administered through 12 district offices.",
    nt: "Significantly stronger than older sources show — civil SOL was eliminated entirely in September 2020. State-administered system simplifies records.",
    src: "CHILD USA SOL Tracker (April 2026)",
    cf: "high",
  },
  NJ: {
    n: "New Jersey",
    a: "DCF/DCPP",
    af: "New Jersey Department of Children and Families, Division of Child Protection and Permanency",
    ad: "Records: Dcfask.Records@dcf.nj.gov. Medical records: DCF.PrivacyOfficer@dcf.nj.gov. Mail: P.O. Box 729, Trenton, NJ 08625",
    p: "(609) 888-7000, (877) 652-2873 (hotline)",
    st: "N.J.S.A. § 9:6-8.10a",
    sc: "Age 55 or 7 years from discovery, whichever later. For adult assault: 7 years.",
    sk: "No SOL for criminal sexual assault.",
    lk: "CLOSED (Dec 2019 - Nov 2021). Age-55 deadline remains permanent.",
    rn: "State-administered. Formerly DYFS (pre-2012). Very restrictive records access — must explain purpose.",
    nt: "Very restrictive records access. Must explain purpose. Lookback window closed 2021. Formerly called DYFS.",
    cf: "high",
    rr: {
      authority: "New Jersey Open Public Records Act (OPRA), N.J.S.A. 47:1A-1 et seq.; N.J.S.A. § 9:6-8.10a (CPS confidentiality)",
      responseDays: 7,
      responseDaysType: "business",
      responseNote: "OPRA requires response within 7 business days. CPS records under § 9:6-8.10a are heavily restricted — the full review can take much longer.",
      formRequired: null,
      formUrl: null,
      idRequired: true,
      idNote: "ID required. NJ DCF requires you to specifically explain the purpose of the request.",
      thirdParty: "court_order",
      thirdPartyNote: "New Jersey is one of the most restrictive states. Even adult subjects requesting their own childhood records are required to explain their purpose. Third-party requests by family members typically require a court order.",
      submitMethods: ["email", "mail"],
      submitEmail: "Dcfask.Records@dcf.nj.gov",
      submitPortal: null,
      notes: "Be prepared to explain WHY you want the records — NJ DCF asks this and may reject vague or 'I'm just curious' requests. Therapeutic, legal, or self-knowledge purposes are typically accepted with a brief explanation. Medical records have a separate inbox: DCF.PrivacyOfficer@dcf.nj.gov. Older records may still be filed under the agency's prior name (DYFS, pre-2012).",
      verified: false,
      source: "NJ DCF records request guidance — verify before sending.",
    },
  },
  NM: {
    n: "New Mexico",
    a: "CYFD",
    af: "New Mexico Children, Youth and Families Department",
    ad: "CYFD, P.O. Drawer 5160, Santa Fe, NM 87502",
    p: "(505) 827-8400, (855) 333-7233 (hotline)",
    st: "NMSA § 32A-4-33",
    sc: "Age 24 or 3 years after first disclosing to a licensed provider. Disclosure-based trigger is unusual.",
    sk: "No SOL for criminal sexual penetration of child under 13.",
    lk: "No lookback window.",
    rn: "State-administered through 5 regions.",
    nt: "Unusual disclosure-based SOL trigger — 3 years from first disclosure to a provider.",
    cf: "medium",
  },
  NV: {
    n: "Nevada",
    a: "DCFS",
    af: "Nevada Division of Child and Family Services",
    ad: "DCFS, 4126 Technology Way, 3rd Floor, Carson City, NV 89706. Clark County and Washoe County operate independently.",
    p: "(775) 684-4400, (800) 992-5757 (hotline)",
    st: "NRS 432B.280",
    sc: "No SOL — permanently and retroactively eliminated in 2021. File at any time.",
    sk: "No SOL for sexual assault of child under 16.",
    lk: "PERMANENT lookback — retroactive SOL elimination 2021. No deadline.",
    rn: "Split: Clark County (Vegas) and Washoe County (Reno) operate own agencies. State DCFS covers rural counties.",
    nt: "One of best states since 2021. Contact correct agency — Clark County, Washoe County, or state DCFS.",
    cf: "high",
  },
  NY: {
    n: "New York",
    a: "OCFS/ACS",
    af: "New York Office of Children and Family Services (county-administered; NYC: Administration for Children's Services)",
    ad: "County-administered. NYC: ACS, 150 William Street, New York, NY 10038. State: OCFS, 52 Washington Street, Rensselaer, NY 12144",
    p: "NYC: (212) 341-0900. State: (518) 473-7793. Hotline: (800) 342-3720",
    st: "NY CPLR § 214-g (Child Victims Act); NYC Admin. Code § 10-1101 et seq. (GMVA); NY Social Services Law § 422",
    sc: "Statewide: age 55 under the Child Victims Act. NYC ONLY: a 2-year revival window under the Gender-Motivated Violence Act (GMVA) opened March 1, 2023 (verify the current closing date with an attorney — this is a NYC-specific path against individuals AND institutions).",
    sk: "No SOL for first-degree rape, criminal sexual act, or course of sexual conduct against a child.",
    lk: "State-level Child Victims Act revival window CLOSED. NYC Gender-Motivated Violence Act window OPENED March 1, 2023 — confirm current status with an attorney before relying on it. Statewide age-55 deadline remains permanent for new claims.",
    rn: "County-administered (58 counties). NYC has ACS. Rest of state through county DSS. OCFS provides state oversight.",
    nt: "DATA UPDATED. Earlier version of this page listed the NYC GMVA window as March 2026-2027; CHILD USA's April 2026 review shows it opened March 1, 2023. Verify the current closing date before filing. Multiple 2025 bills (SB 116, A01641, SB 6978) would expand statewide.",
    src: "CHILD USA SOL Tracker (April 2026)",
    cf: "medium",
    rr: {
      authority: "New York Freedom of Information Law (FOIL), Public Officers Law §§ 84-90; NY Social Services Law § 422 (CPS records confidentiality)",
      responseDays: 5,
      responseDaysType: "business",
      responseNote: "FOIL requires acknowledgment within 5 business days. The actual records release usually takes 20+ business days because CPS records require a separate confidentiality review under SSL § 422.",
      formRequired: "OCFS-2706 (Statewide Central Register / SCR check) — only if requesting your CPS history record",
      formUrl: "https://ocfs.ny.gov/forms/ocfs/OCFS-2706.pdf",
      formNote: "Form OCFS-2706 is for SCR (central registry) checks. For full case files, a written request to the county DSS or NYC ACS is the path.",
      idRequired: true,
      idNote: "Notarized signature and photo ID typically required for SCR record requests.",
      thirdParty: "limited",
      thirdPartyNote: "Under SSL § 422, access is restricted by category. Subjects of reports, parents/legal guardians, the subject's attorney, and family court judges can usually access. Other parties typically need a court order.",
      submitMethods: ["mail"],
      submitEmail: null,
      submitPortal: null,
      notes: "NEW YORK IS SPLIT. NYC residents request from ACS (Administration for Children's Services) at 150 William Street, NYC 10038. Everyone else requests from their county DSS. State OCFS handles the SCR (central registry) but not local case files.",
      verified: false,
      source: "OCFS records guidance + NYC ACS guidance. Verify the right custodian (county DSS vs ACS vs OCFS) before sending.",
    },
  },
  OH: {
    n: "Ohio",
    a: "ODJFS/PCSA",
    af: "Ohio Department of Job and Family Services (county-administered through Public Children Services Agencies)",
    ad: "County-administered — contact your county PCSA. State: ODJFS, 30 East Broad Street, Columbus, OH 43215",
    p: "Varies by county. State: (614) 466-6282. Hotline: (855) 642-4453",
    st: "ORC § 2305.111 (CSA SOL); ORC § 5153.17; Scout's Honor Law (RC 2305.117)",
    sc: "Age 30 for general CSA civil claims — one of shortest extended SOLs. Separate active revival path for BSA-related claims (see below).",
    sk: "No SOL for rape of a child under 13.",
    lk: "NARROW LOOKBACK OPEN: Scout's Honor Law created a 5-year revival window opened October 12, 2023 (running through October 2028) for previously expired claims against the Boy Scouts of America bankruptcy estate. No general retroactive lookback.",
    rn: "County-administered through 88 county PCSAs.",
    nt: "Short general SOL (age 30) — but if your abuse involved BSA, the Scout's Honor Law revival window is open through October 2028.",
    src: "CHILD USA SOL Tracker (April 2026); Ohio Scout's Honor Law",
    cf: "high",
    rr: {
      authority: "Ohio Public Records Act, ORC § 149.43; CPS confidentiality, ORC § 5153.17",
      responseDays: null,
      responseDaysType: null,
      responseNote: "Ohio's Public Records Act requires a response within a 'reasonable period' — there's no fixed statutory deadline. CPS records under § 5153.17 are heavily restricted and the review can take weeks.",
      formRequired: null,
      formUrl: null,
      idRequired: true,
      idNote: "Most counties require photo ID for records that identify the subject.",
      thirdParty: "limited",
      thirdPartyNote: "CPS records are confidential. Parents, legal guardians, attorneys, and the subject themselves typically qualify. Other family members generally need a court order.",
      submitMethods: ["mail"],
      submitEmail: null,
      submitPortal: null,
      notes: "OHIO IS COUNTY-ADMINISTERED through 88 county Public Children Services Agencies (PCSAs). The state ODJFS does not hold case records — you must contact the specific county PCSA. Find your county's PCSA at jfs.ohio.gov.",
      verified: false,
      source: "Ohio Public Records Act + ORC § 5153.17. Verify your county's specific submission process.",
    },
  },
  OK: {
    n: "Oklahoma",
    a: "DHS",
    af: "Oklahoma Department of Human Services, Child Welfare Services",
    ad: "DHS, 2400 North Lincoln Boulevard, Oklahoma City, OK 73105",
    p: "(405) 521-3646, (800) 522-3511 (hotline)",
    st: "Oklahoma Statutes Title 10A § 1-6-102; HB 1935 (2025)",
    sc: "Civil SOL extended significantly by HB 1935 (2025), effective November 1, 2025: filing window extended to age 45 for certain childhood sexual abuse claims. (Older 2-year-from-injury rule may still apply to other claim types — verify with an attorney.)",
    sk: "No SOL for rape, rape by instrumentation, or lewd acts with child under 16. HB 1935 (2025) also expanded prosecution rules — charges may be filed at any time when DNA evidence is available.",
    lk: "No traditional retroactive lookback. HB 1935 extends the standard SOL prospectively to age 45.",
    rn: "State-administered through district offices.",
    nt: "Significantly improved as of November 2025 (HB 1935). Oklahoma Indian Legal Services important for Native American population.",
    src: "CHILD USA SOL Tracker (April 2026); Oklahoma HB 1935 (2025)",
    cf: "high",
  },
  OR: {
    n: "Oregon",
    a: "OCW",
    af: "Oregon Office of Child Welfare",
    ad: "DHS, 500 Summer Street NE, E-15, Salem, OR 97301",
    p: "(503) 945-5944, (855) 503-7233 (hotline)",
    st: "ORS 12.117 (CSA SOL); ORS 419B.035",
    sc: "For incidents on or after June 26, 2025: NO civil SOL. For incidents before June 26, 2025: filing until age 40 or 5 years from discovery, whichever is later — and a permanent revival window has been open since 2010 reviving expired claims against perpetrators and private organizations to age 40.",
    sk: "No SOL for first-degree rape, sodomy, sexual abuse when victim under 18.",
    lk: "PERMANENT lookback window OPEN since 2010 — revives expired CSA claims against perpetrators and private organizations to age 40. Plus prospective elimination of all civil SOL for incidents on/after June 26, 2025.",
    rn: "State-administered through 16 district offices.",
    nt: "One of the stronger states. As of June 26, 2025, no civil SOL for new abuse. For older abuse, the 2010 permanent revival to age 40 still applies. State-administered simplifies records.",
    src: "CHILD USA SOL Tracker (April 2026); 2025 Oregon legislation",
    cf: "high",
  },
  PA: {
    n: "Pennsylvania",
    a: "DHS/CYS",
    af: "Pennsylvania Department of Human Services (county-administered through Children and Youth Services)",
    ad: "County-administered — contact your county CYS. State: DHS, 625 Forster Street, Harrisburg, PA 17120. ChildLine: P.O. Box 2675, Harrisburg, PA 17105",
    p: "Varies by county. State: (717) 787-4756. ChildLine: (800) 932-0313",
    st: "23 Pa.C.S. § 6340",
    sc: "Age 55 (forward-looking only). NO lookback window despite years of attempts — blocked repeatedly in legislature.",
    sk: "No SOL for rape, involuntary deviate sexual intercourse, or sexual assault of child.",
    lk: "NO lookback window. Blocked repeatedly in Pennsylvania legislature. Major political battle.",
    rn: "County-administered through 67 county CYS agencies. ChildLine maintains central registry.",
    nt: "Age-55 SOL is strong but forward-only. Lookback window repeatedly blocked. County system for records.",
    cf: "medium",
    rr: {
      authority: "Pennsylvania Right-to-Know Law, 65 P.S. § 67.101 et seq.; Child Protective Services Law, 23 Pa.C.S. § 6340 (CPS confidentiality)",
      responseDays: 5,
      responseDaysType: "business",
      responseNote: "PA Right-to-Know Law requires a response within 5 business days, with a possible 30-day extension. CPS records under 23 Pa.C.S. § 6340 may take longer due to confidentiality review.",
      formRequired: "Standard PA Right-to-Know Request Form (available on Office of Open Records site)",
      formUrl: "https://www.openrecords.pa.gov/RTKL/StandardForm.cfm",
      formNote: "PA agencies prefer the standard Right-to-Know request form. Submit it to your county CYS, not the state DHS.",
      idRequired: true,
      idNote: "ID required by most counties.",
      thirdParty: "limited",
      thirdPartyNote: "Restricted to subjects, parents, legal guardians, attorneys, and named perpetrators (for their own records). Other relatives generally need a court order.",
      submitMethods: ["mail", "email", "fax"],
      submitEmail: null,
      submitPortal: null,
      notes: "PENNSYLVANIA IS COUNTY-ADMINISTERED through 67 county Children and Youth Services (CYS) agencies. The state DHS does NOT hold case records — you must contact the county CYS where the case occurred. Find your county at dhs.pa.gov. ChildLine maintains the central registry; case files are with the county.",
      verified: false,
      source: "PA Office of Open Records + 23 Pa.C.S. § 6340. Verify your county's specific submission process.",
    },
  },
  RI: {
    n: "Rhode Island",
    a: "DCYF",
    af: "Rhode Island Department of Children, Youth and Families",
    ad: "DCYF, 101 Friendship Street, Providence, RI 02903",
    p: "(401) 528-3502, (800) 742-4453 (hotline)",
    st: "RIGL § 9-1-51 (revival); RIGL § 40-11-13",
    sc: "7 years from discovery for the standard SOL — but a permanent revival window opened in 2019 allows survivors to file childhood sexual abuse claims against a perpetrator until age 53.",
    sk: "No SOL for first-degree sexual assault of a child.",
    lk: "PERMANENT lookback window OPEN since 2019 — revives expired CSA claims against the perpetrator only (not against institutions). Survivors can file until age 53.",
    rn: "State-administered. Small state — centralized.",
    nt: "Stronger than the headline 7-year discovery rule suggests — the 2019 permanent revival lets survivors file against perpetrators until age 53. Window does not cover institutional defendants. Small state simplifies records access.",
    src: "CHILD USA SOL Tracker (April 2026)",
    cf: "high",
  },
  SC: {
    n: "South Carolina",
    a: "DSS",
    af: "South Carolina Department of Social Services",
    ad: "DSS, P.O. Box 1520, Columbia, SC 29202",
    p: "(803) 898-7601, (888) 227-3487 (hotline)",
    st: "SC Code § 63-7-1990",
    sc: "6 years from age 18 (until age 24) or from discovery. Relatively short.",
    sk: "No SOL for criminal sexual conduct with a minor in first degree.",
    lk: "No lookback window.",
    rn: "Operates through 46 county offices.",
    nt: "Short SOL (age 24). Children's Law Center at USC is key resource.",
    cf: "medium",
  },
  SD: {
    n: "South Dakota",
    a: "CPS",
    af: "South Dakota Division of Child Protection Services",
    ad: "DSS, 700 Governors Drive, Pierre, SD 57501",
    p: "(605) 773-3227",
    st: "SDCL § 26-8A-13",
    sc: "3 years from act or discovery. Short with limited minor extensions.",
    sk: "No SOL for rape or sexual contact with child under 16.",
    lk: "No lookback window.",
    rn: "State-administered.",
    nt: "Short SOL. Significant Native American population — ICWA applies.",
    cf: "medium",
  },
  TN: {
    n: "Tennessee",
    a: "DCS",
    af: "Tennessee Department of Children's Services",
    ad: "DCS, 315 Deaderick Street, Nashville, TN 37243",
    p: "(615) 741-9701, (877) 237-0004 (hotline)",
    st: "TCA § 28-3-116 (CSA SOL); TCA § 37-1-612; HB 973 (2025)",
    sc: "Civil SOL: age 33 or 3 years from discovery (extended 2019). SB 1070 (introduced 2025) would extend civil SOL to age 48 — pending.",
    sk: "HB 973 enacted 2025 (effective July 1, 2025) extended the criminal SOL for child sexual abuse, but requires corroborating evidence for prosecutions initiated long after the survivor reaches adulthood.",
    lk: "No traditional retroactive lookback window.",
    rn: "State-administered through 12 regional offices.",
    nt: "Civil SOL still age 33 (pending 2025 expansion). Criminal SOL extended in 2025 with corroborating-evidence requirement. Claims Commission required for state defendant claims.",
    src: "CHILD USA SOL Tracker (April 2026); Tennessee HB 973 (2025)",
    cf: "high",
  },
  TX: {
    n: "Texas",
    a: "DFPS",
    af: "Texas Department of Family and Protective Services",
    ad: "DFPS, P.O. Box 149030, Austin, TX 78714. Email: open.records@dfps.texas.gov",
    p: "(512) 438-4800, (800) 252-5400 (hotline)",
    st: "Texas Family Code § 261.201; Tex. Civ. Prac. & Rem. Code § 16.0045; SB 2798 (2025)",
    sc: "Civil SOL extended to 20 years from age 18 (until age 38) for victims under 17, by SB 2798 effective September 1, 2025. Civil SOL is NOT fully eliminated — verify the cutoff date that applies to your case with an attorney.",
    sk: "No SOL for sexual assault of a child, aggravated sexual assault, continuous sexual abuse, or indecency with a child (criminal SOL eliminated by SB 2798, effective Sept 1, 2025).",
    lk: "No traditional retroactive lookback window. SB 2798 extended SOL prospectively only.",
    rn: "State-administered through 11 regions. Records via open records request to DFPS.",
    nt: "FLAG: prior version of this page incorrectly stated civil SOL was eliminated. SB 2798 (2025) extended civil SOL to age 38 and eliminated CRIMINAL SOL — but did not eliminate civil SOL. Get an attorney to confirm which version applies to your case.",
    src: "CHILD USA SOL Tracker (April 2026); Texas SB 2798 (2025). Needs primary-source verification before relying on for filing decisions.",
    cf: "medium",
    rr: {
      authority: "Texas Public Information Act, Government Code Chapter 552; Texas Family Code § 261.201 (CPS confidentiality)",
      responseDays: 10,
      responseDaysType: "business",
      responseNote: "Under the Texas Public Information Act, custodians have 10 business days to respond. Sensitive child welfare records may take longer due to the separate confidentiality review under § 261.201.",
      formRequired: null,
      formUrl: null,
      idRequired: true,
      idNote: "Photo ID is typically required to release records that identify the subject. Include a copy with your request.",
      thirdParty: "limited",
      thirdPartyNote: "CPS records are confidential under § 261.201. Third-party requests are limited to parents, legal guardians, the subject's attorney, or those with a court order. A non-custodial relative will typically need a court order.",
      submitMethods: ["email", "mail"],
      submitEmail: "open.records@dfps.texas.gov",
      submitPortal: null,
      notes: "DFPS is state-administered, so a single request reaches the right office. Email is the preferred channel.",
      verified: false,
      source: "DFPS records request guidance — verify at dfps.texas.gov before sending.",
    },
  },
  UT: {
    n: "Utah",
    a: "DCFS",
    af: "Utah Division of Child and Family Services",
    ad: "DCFS, 195 North 1950 West, Salt Lake City, UT 84116",
    p: "(801) 538-4100, (855) 323-3237 (hotline)",
    st: "Utah Code § 78B-2-308; Utah Code § 80-2-1001 et seq.",
    sc: "NO civil SOL for intentional or negligent sexual abuse of a minor when filing AGAINST THE PERPETRATOR — eliminated effective March 23, 2015. Claims against institutions (other defendants) still subject to standard 4-year SOL from age 18 or discovery.",
    sk: "No SOL for first-degree felony sexual abuse of a child.",
    lk: "No active retroactive lookback. The 2016 revival window was held UNCONSTITUTIONAL by the Utah Supreme Court in 2020.",
    rn: "State-administered through 5 regions.",
    nt: "Important nuance: NO civil SOL when suing the perpetrator directly (since 2015), but the standard 4-year-from-age-18 SOL still applies to institutional defendants. The 2016 retroactive revival was struck down. GRAMA provides structured records framework.",
    src: "CHILD USA SOL Tracker (April 2026)",
    cf: "high",
  },
  VA: {
    n: "Virginia",
    a: "VDSS",
    af: "Virginia Department of Social Services",
    ad: "Locally administered — contact local DSS. State FOIA (not for case records): VDSS, 5600 Cox Road, Glen Allen, VA 23060",
    p: "(804) 726-7000, (800) 552-7096 (hotline)",
    st: "Virginia Code § 63.2-104",
    sc: "20 years from age 18 (until age 38). Only applies to individuals — NOT institutions. D grade from Child USA.",
    sk: "No SOL for felony child sexual abuse.",
    lk: "NO lookback window. Multiple bills failed. SOL excludes institutions — major barrier.",
    rn: "Locally administered through 120 local DSS. VDSS does NOT hold case records — must contact local LDSS.",
    nt: "D-rated civil SOL. Excludes institutions. Must contact local LDSS, not state VDSS.",
    cf: "high",
  },
  VT: {
    n: "Vermont",
    a: "DCF",
    af: "Vermont Department for Children and Families",
    ad: "DCF, 280 State Drive, HC 1 North, Waterbury, VT 05671. Records access: OCYFA@vermont.gov",
    p: "(802) 241-0929, (800) 649-5285 (hotline)",
    st: "12 V.S.A. § 522 (civil SOL); 33 V.S.A. § 4913; H.644 (2024)",
    sc: "NO civil SOL — eliminated effective July 1, 2019, fully retroactive. Survivors can file at any time.",
    sk: "6 years for aggravated sexual assault.",
    lk: "PERMANENT lookback window OPEN since 2019. Revives all expired claims against any defendant.",
    rn: "State-administered. H.644 (2024) grants former foster youth FULL access to DCF AND court records at age 18 — leads nation.",
    nt: "One of the strongest states. No civil SOL since July 2019 plus a permanent open revival window. H.644 also gives former foster youth full records access — contact OCYFA@vermont.gov.",
    src: "CHILD USA SOL Tracker (April 2026)",
    cf: "high",
  },
  WA: {
    n: "Washington",
    a: "DCYF",
    af: "Washington Department of Children, Youth, and Families",
    ad: "DCYF, 1500 Jefferson Street SE, Olympia, WA 98501. Email: publicdisclosure@dcyf.wa.gov",
    p: "(866) 363-4276, (866) 829-2153 (hotline)",
    st: "RCW 4.16.340; RCW 13.50; RCW 26.44",
    sc: "For incidents on or after June 6, 2024: NO civil SOL — eliminated. For incidents before June 6, 2024: 3 years from act or 3 years from discovery (discovery rule is key for older claims).",
    sk: "No SOL for first-degree child rape or molestation.",
    lk: "Civil SOL ELIMINATED prospectively for incidents on/after June 6, 2024. No retroactive lookback window — discovery rule still controls older claims.",
    rn: "State-administered (DCYF created 2018, replacing Children's Administration within DSHS). Pre-2018 records may reference DSHS.",
    nt: "Significantly improved June 2024 — no civil SOL for new abuse. Older abuse still subject to discovery-rule SOL. Pre-2018 records under old DSHS system.",
    src: "CHILD USA SOL Tracker (April 2026)",
    cf: "high",
  },
  WI: {
    n: "Wisconsin",
    a: "DCF",
    af: "Wisconsin Department of Children and Families",
    ad: "DCF, 201 East Washington Avenue, 2nd Floor, Madison, WI 53703. County-administered except Milwaukee.",
    p: "(608) 422-7000",
    st: "Wisconsin Statutes § 48.981(7)",
    sc: "Age 35 (effective 2004). Discovery rule applies.",
    sk: "No SOL for first-degree sexual assault of a child. Second-degree: before age 45.",
    lk: "No lookback window.",
    rn: "County-administered except Milwaukee (DCF direct). Contact county department.",
    nt: "Moderate SOL (age 35). Milwaukee is only county where DCF administers directly.",
    cf: "medium",
  },
  WV: {
    n: "West Virginia",
    a: "BSS",
    af: "West Virginia Bureau for Social Services",
    ad: "BSS, 350 Capitol Street, Room 691, Charleston, WV 25301",
    p: "(304) 558-0999, (800) 352-6513 (hotline)",
    st: "WV Code § 55-2-15 (CSA revival); WV Code § 49-5-101",
    sc: "2 years from discovery as the standard SOL — but a permanent revival window opened in 2020 allows survivors of childhood sexual abuse to file until age 36 against the perpetrator and private organizations.",
    sk: "No SOL for sexual assault of child (first or second degree).",
    lk: "PERMANENT lookback window OPEN since 2020 — revives expired CSA claims against perpetrators and private organizations until the survivor reaches age 36.",
    rn: "State-administered through regional offices.",
    nt: "Better than the 2-year discovery SOL suggests — the 2020 permanent revival lets survivors file until age 36. Court of Claims required for state defendant claims.",
    src: "CHILD USA SOL Tracker (April 2026)",
    cf: "high",
  },
  WY: {
    n: "Wyoming",
    a: "DFS",
    af: "Wyoming Department of Family Services",
    ad: "DFS, 2300 Capitol Avenue, Hathaway Building, 3rd Floor, Cheyenne, WY 82002",
    p: "(307) 777-7561",
    st: "WS § 14-3-214",
    sc: "8 years from age 18 (until age 26) or from discovery.",
    sk: "No SOL for first or second degree sexual abuse of a minor.",
    lk: "No lookback window.",
    rn: "State-administered through 5 districts.",
    nt: "Moderate SOL (age 26). Small state — records relatively centralized.",
    cf: "medium",
  },
};
